import { useEffect } from 'react';
import { rootStore } from '@/stores';

// Global keyboard shortcuts. Registered once at app boot.
// Convention: ignore key events when the focus is in an input/textarea/contentEditable
// EXCEPT for Cmd/Ctrl+K which we always honor (so the palette is reachable from
// anywhere, including the search input).

export interface ShortcutHandler {
  key: string;            // e.g. 'k', '?', '/'
  meta?: boolean;         // require Cmd or Ctrl
  shift?: boolean;
  ignoreInInputs?: boolean;
  description: string;
  handler: (e: KeyboardEvent) => void;
}

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return target.isContentEditable;
}

function matches(e: KeyboardEvent, h: ShortcutHandler): boolean {
  if (h.key.toLowerCase() !== e.key.toLowerCase()) return false;
  if (h.meta && !(e.metaKey || e.ctrlKey)) return false;
  if (!h.meta && (e.metaKey || e.ctrlKey)) return false;
  if (h.shift !== undefined && h.shift !== e.shiftKey) return false;
  return true;
}

const handlers: ShortcutHandler[] = [];

export function registerShortcut(h: ShortcutHandler): () => void {
  handlers.push(h);
  return () => {
    const i = handlers.indexOf(h);
    if (i !== -1) handlers.splice(i, 1);
  };
}

export function listShortcuts(): readonly ShortcutHandler[] {
  return handlers;
}

let installed = false;

export function installGlobalShortcuts(onShowCheatsheet: () => void): void {
  if (installed) return;
  installed = true;

  // Cmd+K / Ctrl+K — open command palette. Always honored.
  registerShortcut({
    key: 'k',
    meta: true,
    description: 'Open command palette',
    handler: (e) => {
      e.preventDefault();
      rootStore.commandPaletteStore.toggle();
    },
  });

  // ? — show keyboard cheatsheet (only when not typing).
  registerShortcut({
    key: '?',
    shift: true,
    ignoreInInputs: true,
    description: 'Show keyboard shortcuts',
    handler: (e) => {
      e.preventDefault();
      onShowCheatsheet();
    },
  });

  // Cmd+Shift+L — toggle theme.
  registerShortcut({
    key: 'l',
    meta: true,
    shift: true,
    description: 'Toggle light/dark mode',
    handler: (e) => {
      e.preventDefault();
      rootStore.themeStore.toggleMode();
    },
  });

  document.addEventListener('keydown', (e) => {
    for (const h of handlers) {
      if (h.ignoreInInputs && isEditable(e.target)) continue;
      if (matches(e, h)) {
        h.handler(e);
        return;
      }
    }
  });
}

// React hook variant — handy when a component wants a one-off shortcut while mounted.
export function useShortcut(h: ShortcutHandler): void {
  useEffect(() => registerShortcut(h), [h]);
}
